import React from "react";
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Unstable_Grid2';
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from '../../zeon-react-ui/auth/model';
import { IFSUser } from "../store";
import TextInput from "../../zeon-react-ui/input/TextInput";
import BooleanInput from "../../zeon-react-ui/input/BooleanInput";
import Button, { ButtonType } from "../../zeon-react-ui/button/Button";

const _API_URL_ = "/api/system/users";

const _BLOCK_TITLE_ = "User Parameters";
const _EDIT_TITLE_ = "Changing params for user: %s";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

const TablePaginationActions = (props: TablePaginationActionsProps) => {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        <FirstPageIcon />
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page" >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        <KeyboardArrowRight />
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

interface IEditAction {
  (username: string): void;
}
interface IBrowseAction {
  (): void;
}
interface IAddAction {
  (): void;
}
interface IBrowseProps {
  rows: IFSUser[],
  onEdit: IEditAction
  onAdd: IAddAction
}
interface IEditProps {
  data: IFSUser | null,
  onBrowse: IBrowseAction
}
interface IAddProps {
  onBrowse: IBrowseAction,
  onEdit: IEditAction
}

interface IServerAnswer {
  success: boolean,
  payload: any
}

const Browse = (props: IBrowseProps) => {
  const { rows, onEdit, onAdd } = props;

  const rowsPerPage = 5;
  const [page, setPage] = React.useState(0);
  const emptyRows = rows.length > 0 ? Math.max(0, ((page + 1) * rowsPerPage) - rows.length) : rowsPerPage;
  const handleChangePage = (event: unknown, newPage: number) => { setPage(newPage); };

  return (
    <Container sx={{ m: 0, p: 0, mb: 1 }}>
      <Grid container spacing={2} sx={{ borderColor: "primary.main", borderWidth: 1, borderStyle: "solid", p: 0, pr: 2, m: 0 }}>
        <Grid xs={12}>
          <Box sx={{ minWidth: 1, bgcolor: "primary.main", color: "white", p: 1 }}>
            <Typography><b>{_BLOCK_TITLE_}</b></Typography>
          </Box>
        </Grid>
        <Table sx={{ minWidth: 100, width: "100%" }} aria-label="Enabled Users">
          <TableBody>
            {(rowsPerPage > 0
              ? (rows ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [])
              : rows || []
            ).map((row) => (
              <TableRow key={row.username}>
                <TableCell component="th" scope="row">
                  <a href="#" onClick={() => (onEdit(row.username))}>{row.username}</a>
                </TableCell>
                <TableCell align="center">
                  {row.params.sites.join(" ")}
                </TableCell>
                <TableCell align="center">
                  {row.params.locations.join(" ")}
                </TableCell>
                <TableCell align="center">
                  {row.params.commodities.join(" ")}
                </TableCell>
                <TableCell align="center">
                  {row.params.diba.bom ? "Bom" : ""}&nbsp;
                  {row.params.diba.routings ? "Route" : ""}&nbsp;
                  {row.params.diba.buyer ? "Buy" : ""}&nbsp;
                  {row.params.diba.costs ? "Cost" : ""}&nbsp;
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <Box sx={{ mt: 1, ml: 1 }}>
                <Button label="Add" leftIcon={<AddIcon />} onClick={onAdd} />
              </Box>
              <TablePagination
                rowsPerPageOptions={[5]}
                colSpan={3}
                count={rows ? rows.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page', },
                  native: true,
                }}
                onPageChange={handleChangePage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Grid>
    </Container>
  );
}

const Edit = (props: IEditProps) => {
  const { data, onBrowse } = props;

  const [error, setError] = React.useState("");
  const [disabled, setDisabled] = React.useState(false);
  const [userSites, setUserSites] = React.useState(`${data?.params?.sites.join(" ") || ""}`);
  const [userCommodities, setUserCommodities] = React.useState(`${data?.params?.commodities.join(" ") || ""}`);
  const [userLocations, setUserLocations] = React.useState(`${data?.params?.locations.join(" ") || ""}`);
  const [defaultSite, setDefaultSite] = React.useState(`${data?.params?.defaults.site || ""}`);
  const [defaultLocation, setDefaultLocation] = React.useState(`${data?.params?.defaults.location || ""}`);
  const [defaultCommodity, setDefaultCommodity] = React.useState(`${data?.params?.defaults.commodity || ""}`);
  const [dibaBOM, setDibaBOM] = React.useState(Boolean(data?.params?.diba.bom || false));
  const [dibaBuyer, setDibaBuyer] = React.useState(Boolean(data?.params?.diba.buyer || false));
  const [dibaCosts, setDibaCosts] = React.useState(Boolean(data?.params?.diba.costs || false));
  const [dibaRoutings, setDibaRoutings] = React.useState(Boolean(data?.params?.diba.routings || false));
  const [dismagTiming, setDismagTiming] = React.useState(Boolean(data?.params?.dismag.timing || false));
  const [dismagQuantity, setDismagQuantity] = React.useState(Boolean(data?.params?.dismag.quantity || false));
  const [dismagOnlyOnHand, setDismagOnlyOnHand] = React.useState(Boolean(data?.params?.dismag.only_on_hand || false));

  const handleCancelButton = () => {
    onBrowse();
  }

  const handleRemoveButton = () => {
    if (!data?.username) return;
    setDisabled(true);
    axios.delete(`${_API_URL_}/user/${data?.username}`).then(onBrowse);
  }

  const handleApplyButton = () => {
    const payload = Object.assign({}, data, {
      params: {
        sites: userSites.split(" ").map(one => (one.trim())),
        commodities: userCommodities.split(" ").map(one => (one.trim())),
        locations: userLocations.split(" ").map(one => (one.trim())),
        defaults: {
          site: defaultSite,
          location: defaultLocation,
          commodity: defaultCommodity
        },
        diba: {
          bom: dibaBOM,
          buyer: dibaBuyer,
          costs: dibaCosts,
          routings: dibaRoutings
        },
        dismag: {
          timing: dismagTiming,
          quantity: dismagQuantity,
          only_on_hand: dismagOnlyOnHand
        }
      }
    });
    setDisabled(true);
    axios.post(`${_API_URL_}/user`, payload).then(answer => {
      setDisabled(false);
      if (answer.data.success) onBrowse();
      else setError(answer.data?.payload);
    }).catch(e => {
      setDisabled(false);
      console.error(e);
      setError(e.message);
    });
  }

  const handleCloseError = () => {
    setError("");
  }

  if (data === null) return null;

  return (
    <Container>
      <Grid container spacing={2} sx={{ borderColor: "primary.main", borderWidth: 1, borderStyle: "solid", p: 0, pr: 2, m: 0 }}>

        <Grid xs={12}>
          <Box sx={{ minWidth: 1, bgcolor: "primary.main", color: "white", p: 1 }}>
            <Typography>{_EDIT_TITLE_.replace("%s", data.username)}</Typography>
          </Box>
        </Grid>

        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="User Sites" value={userSites} onChange={setUserSites} disabled={disabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="User Locations (* for all)" value={userLocations} onChange={setUserLocations} disabled={disabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="User Commodities (* for all, @ for KIT)" value={userCommodities} onChange={setUserCommodities} disabled={disabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="Default Site" value={defaultSite} onChange={setDefaultSite} disabled={disabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="Default Location" value={defaultLocation} onChange={setDefaultLocation} disabled={disabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="Default Commodity" value={defaultCommodity} onChange={setDefaultCommodity} disabled={disabled} />
        </Grid>

        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DiBa -> BOM" value={dibaBOM} onChange={setDibaBOM} swapLabel={false} />
        </Grid>
        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DiBa -> Buyer" value={dibaBuyer} onChange={setDibaBuyer} swapLabel={false} />
        </Grid>
        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DiBa -> Costs" value={dibaCosts} onChange={setDibaCosts} swapLabel={false} />
        </Grid>
        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DiBa -> Routings" value={dibaRoutings} onChange={setDibaRoutings} swapLabel={false} />
        </Grid>

        <Grid xs={12}><hr /></Grid>

        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DisMag -> Time" value={dismagTiming} onChange={setDismagTiming} swapLabel={false} helpText="Show totals on last date"/>
        </Grid>
        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DisMag -> Quantity" value={dismagQuantity} onChange={setDismagQuantity} swapLabel={false} helpText="Show real quantity if less than 10 or >10 if more than 10"/>
        </Grid>
        <Grid xs={3} sx={{ ml: 1, mr: 0 }}>
          <BooleanInput label="DisMag -> On Hand" value={dismagOnlyOnHand} onChange={setDismagOnlyOnHand} swapLabel={false} helpText="Show only 'on hand' products"/>
        </Grid>

        <Grid xs={12}>
          <Box sx={{ textAlign: "right" }}>
            <Button type={ButtonType.danger} label="Remove" onClick={handleRemoveButton} disabled={disabled} />
            &nbsp;
            <Button type={ButtonType.cancel} label="Cancel" onClick={handleCancelButton} disabled={disabled} />
            &nbsp;
            <Button type={ButtonType.default} label="Apply" onClick={handleApplyButton} disabled={disabled} />
          </Box>
        </Grid>

      </Grid>

      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          <b>{_BLOCK_TITLE_}</b><br />
          {error}
        </Alert>
      </Snackbar>
    </Container >
  );
}

const Add = (props: IAddProps) => {
  const { onEdit, onBrowse } = props;
  const [rows, setRows] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const rowsPerPage = 4;
  const [page, setPage] = React.useState(0);
  const [emptyRows, setEmptyRows] = React.useState(rows.length > 0 ? Math.max(0, ((page + 1) * rowsPerPage) - rows.length) : rowsPerPage);

  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const handleChangePage = (event: unknown, newPage: number) => { setPage(newPage); };

  let searchPattern = "";
  const handleSearchChange = (pattern: string) => {
    setSearch(pattern);
    searchPattern = pattern;
    searchReducer();
  }
  const searchReducer = (toReduce?: []) => {
    let result = [];
    const data = toReduce ? toReduce : rows;
    if (searchPattern === "") result = data;
    else result = data.reduce((out, one) => {
      if (`${one}`.toUpperCase().includes(searchPattern.toUpperCase())) out.push(one);
      return out;
    }, []);
    setUsers(result);
    setEmptyRows(result.length > 0 ? Math.max(0, ((page + 1) * rowsPerPage) - result.length) : rowsPerPage);
    console.log(`reduced=${result.length}, emptyRows=${emptyRows}`);
  }

  const fetch = async () => {
    setLoading(true);
    setLoaded(false);
    axios.get(`${_API_URL_}/addable`).then((answer) => {
      const data: IServerAnswer = answer.data;
      if (data.success) {
        setRows(data.payload);
        searchReducer(data.payload);
        setLoading(false);
        setLoaded(true);
      } else {
        setRows([]);
        setLoading(false);
        setLoaded(true);
        setError(`${data.payload}`);
      }
    })
  }
  if (!loading && !loaded) fetch();

  return (
    <Container sx={{ mb: 1 }}>
      <Grid container spacing={2} sx={{ borderColor: "primary.main", borderWidth: 1, borderStyle: "solid", p: 0, pr: 2, m: 0 }}>
        <Grid xs={12}>
          <Box sx={{ minWidth: 1, bgcolor: "primary.main", color: "white", p: 1 }}>
            <Typography><b>Add User from Active Directory</b></Typography>
          </Box>
        </Grid>
        <Grid xs={12}>
          <TextInput left={<SearchIcon />} label="Search" onChange={handleSearchChange} value={search} />
        </Grid>
        <Table sx={{ minWidth: 100, width: "100%" }} aria-label="Enabled Users">
          <TableBody>
            {(rowsPerPage > 0
              ? (users ? users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [])
              : users || []
            ).map(row => (
              <TableRow key={row}>
                <TableCell component="th" scope="row">
                  <a href="#" onClick={() => (onEdit(row))}>{row}</a>
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <Box sx={{ mt: 1, ml: 1 }}>
                <Button label="Back" leftIcon={<ArrowBackIcon />} onClick={onBrowse} />
              </Box>
              <TablePagination
                rowsPerPageOptions={[rowsPerPage]}
                colSpan={3}
                count={rows ? rows.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page', },
                  native: true,
                }}
                onPageChange={handleChangePage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Grid>
    </Container>
  )
}

const Users = () => {
  const auth = useAuth();
  let navigate = useNavigate();
  let location = useLocation();
  let from = location.state?.from?.pathname || "/";

  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [displayMode, setDisplayMode] = React.useState("browse");

  const fetch = async () => {
    setLoading(true);
    setLoaded(false);
    axios.get(`${_API_URL_}/all`).then((answer) => {
      const data: IServerAnswer = answer.data;
      if (data.success) {
        setRows(data.payload);
        setLoading(false);
        setLoaded(true);
      } else {
        setRows([]);
        setLoading(false);
        setLoaded(true);
        setError(`${data.payload}`);
      }
    })
  }
  if (!loading && !loaded) fetch();

  const [currentUser, setCurrentUser] = React.useState("");
  let userData: IFSUser | null = (currentUser === "") ? null : rows.reduce((out: IFSUser | null, one: IFSUser) => {
    if ((out === null) && (one?.username === currentUser)) out = one;
    return out;
  }, null);
  if (currentUser && (userData === null)) userData = {
    username: currentUser,
    params: {
      defaults: {
        location: "",
        site: "",
        commodity: ""
      },
      commodities: [],
      locations: [],
      sites: [],
      diba: {
        bom: false,
        costs: false,
        buyer: false,
        routings: false
      },
      dismag: {
        timing: false,
        quantity: false,
        only_on_hand: false
      }
    }
  };

  const handleEditUser: IEditAction = (username: string) => {
    setCurrentUser(username);
    setDisplayMode("edit");
  }

  const handleAddUser: IAddAction = () => {
    setCurrentUser("");
    setDisplayMode("add");
  }

  const handleCloseError = () => { setError(""); }

  const children = () => {
    if (displayMode === "browse") return (
      <Browse rows={rows} onEdit={handleEditUser} onAdd={handleAddUser} />
    ); else if (displayMode === "edit") return (
      <Edit data={userData} onBrowse={() => { setDisplayMode("browse"); fetch(); }} />
    ); else if (displayMode === "add") return (
      <Add onBrowse={() => { setDisplayMode("browse"); fetch(); }} onEdit={handleEditUser} />
    ); else return (
      <Container>
        <Grid container spacing={2} sx={{ borderColor: "primary.main", borderWidth: 1, borderStyle: "solid", p: 0, pr: 2, m: 0 }}>

          <Grid xs={12}>
            <Box sx={{ minWidth: 1, bgcolor: "primary.main", color: "white", p: 1 }}>
              <Typography>{_BLOCK_TITLE_}</Typography>
            </Box>
          </Grid>

        </Grid>

      </Container >
    );
  }
  return (
    <Container >
      {children()}
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          <b>{_BLOCK_TITLE_}</b><br />
          {error}
        </Alert>
      </Snackbar>
    </Container >
  );
}

export default Users;